import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { CartService } from '@core/services/cart/cart.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { BasketService } from '@core/services/basket/basket.service';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { of, throwError, timer } from 'rxjs';
import { DoodOrderModel } from '@store/order/order.model';
import { OrderKeys } from '@config/keys/order.keys';
import { catchError, delay, map, mergeMap, takeUntil, tap, timeout } from 'rxjs/operators';
import { PaymentMethodHandles } from '@config/payment-methods.config';
import { DoodApiError } from '@shared/interfaces/error.interface';
import { Paths } from '@config/paths.config';
import { YavinTerminalService } from '@core/services/yavin-terminal/yavin-terminal.service';
import { TerminalAbortService } from '@core/services/yavin-terminal/terminal-abort.service';
import { KioskService } from '@core/services/kiosk/kiosk.service';
import { YavinTransactionResponse } from '@core/services/api/yavin-terminal/yavin-terminal-api.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { logToJitsu } from '@shared/utils/logger/logger.utils';

@Component({
  selector: 'app-payment-panel-yavin-terminal',
  templateUrl: './payment-panel-yavin-terminal.component.html',
  styles: [],
  standalone: false,
})
export class PaymentPanelYavinTerminalComponent extends DestroyerBase implements OnInit, OnDestroy {
  readonly terminalInstructionMessage = input<string>();

  statusMessage?: string;
  errorMessage?: string;
  isPaid = false;
  isOnError = false;
  isLoading = true;
  loadKioskResetTimer = false;

  private readonly router = inject(Router);
  private readonly cartService = inject(CartService);
  private readonly ordersService = inject(OrdersService);
  private readonly basketService = inject(BasketService);
  private readonly paymentDispatcher = inject(PaymentStoreDispatcher);
  private readonly yavinTerminalService = inject(YavinTerminalService);
  private readonly terminalAbortService = inject(TerminalAbortService);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly kioskService = inject(KioskService);
  private readonly analyticsService = inject(AnalyticsService);

  ngOnInit(): void {
    this.terminalAbortService.register(() => {
      logToJitsu(
        this.analyticsService,
        'info',
        'yavin_terminal_abort_requested',
        '[Yavin Terminal] Abort requested by user',
        {},
      );
      return this.yavinTerminalService.abortTransaction$().pipe(
        timeout(10000),
        map(response => {
          const success = response?.status === 'ok';
          logToJitsu(
            this.analyticsService,
            success ? 'info' : 'warn',
            'yavin_terminal_abort_response',
            '[Yavin Terminal] Abort response received',
            { status: response?.status },
          );
          return success;
        }),
        catchError(err => {
          logToJitsu(
            this.analyticsService,
            'error',
            'yavin_terminal_abort_failed',
            '[Yavin Terminal] Abort failed or timed out',
            { reason: err?.message ?? 'unknown' },
          );
          return of(false);
        }),
      );
    });
    timer(1).subscribe(() => {
      this.paymentDispatcher.updateUI({
        error: null,
        button: { isEnabled: false, isVisible: false },
        status: { isLoading: false },
      });
    });
    this.createOrder();
  }

  ngOnDestroy(): void {
    this.terminalAbortService.unregister();
    super.ngOnDestroy();
  }

  createOrder(): void {
    this.isOnError = false;
    this.isLoading = true;
    this.errorMessage = '';
    this.statusMessage = '';
    this.loadKioskResetTimer = false;

    this.ordersService
      .createOrder$(PaymentMethodHandles.YavinTerminal)
      .pipe(
        takeUntil(this._destroyerRef),
        tap(order => {
          if (order) {
            logToJitsu(
              this.analyticsService,
              'info',
              'yavin_terminal_order_created',
              '[Yavin Terminal] Order created',
              { orderId: order[OrderKeys.Id], orderNumber: order[OrderKeys.Number] },
            );
          }
          this.isLoading = false;
        }),
        map(order => (order ? this.startTransaction(order) : null)),
      )
      .subscribe({
        error: (response: { error: DoodApiError }) => {
          this.handleOrderError(response.error);
        },
      });
  }

  private startTransaction(order: DoodOrderModel): void {
    const finalPrice = order[OrderKeys.FinalPrice];

    this.errorMessage = '';
    this.statusMessage = '';

    if (typeof finalPrice !== 'number') {
      this.errorMessage = 'Erreur : montant incorrect';
      logToJitsu(
        this.analyticsService,
        'error',
        'yavin_terminal_invalid_amount',
        '[Yavin Terminal] Invalid amount',
        { orderId: order[OrderKeys.Id], orderNumber: order[OrderKeys.Number] },
      );
      return;
    }

    // Log transaction start
    logToJitsu(
      this.analyticsService,
      'info',
      'yavin_terminal_transaction_start',
      '[Yavin Terminal] Starting transaction',
      {
        amount: finalPrice,
        orderNumber: order[OrderKeys.Number],
        orderId: order[OrderKeys.Id],
      },
    );

    // Abort any existing transaction before starting a new one
    // This prevents "UUID already in progress" errors from stale transactions
    this.yavinTerminalService
      .abortTransaction$()
      .pipe(
        catchError(err => {
          // Ignore abort errors (might not have any transaction to abort)
          logToJitsu(
            this.analyticsService,
            'info',
            'yavin_terminal_abort_cleanup',
            '[Yavin Terminal] Abort transaction (cleanup)',
            {
              reason: err?.message || 'no pending transaction',
              orderId: order[OrderKeys.Id],
            },
          );
          return of(null);
        }),
        delay(500), // Small delay to ensure terminal processes the abort
        mergeMap(() =>
          this.yavinTerminalService.startTransaction$(
            finalPrice,
            order[OrderKeys.Number],
            order[OrderKeys.Id],
          ),
        ),
        tap(transactionResponse => {
          // Log transaction response
          logToJitsu(
            this.analyticsService,
            transactionResponse.status === 'ok' ? 'info' : 'warn',
            'yavin_terminal_transaction_response',
            '[Yavin Terminal] Transaction response',
            {
              status: transactionResponse.status,
              transactionId: transactionResponse.transactionId,
              amount: transactionResponse.amount,
              message: transactionResponse.message,
              orderId: order[OrderKeys.Id],
            },
          );

          this.isLoading = false;

          if (transactionResponse.status === 'ko') {
            this.handlePaymentError(transactionResponse.message ?? 'Erreur', order);
          }

          if (transactionResponse.status === 'ok') {
            this.errorMessage = undefined;
            this.handlePaymentSuccess(order, transactionResponse);
          }

          if (transactionResponse.message) {
            this.statusMessage = transactionResponse.message;
          }
        }),
        catchError(err => {
          this.handlePaymentError('Impossible de communiquer avec le terminal de paiement.', order);
          return throwError(() => err);
        }),
      )
      .subscribe();
  }

  private handlePaymentError(errorMessage: string, order?: DoodOrderModel): void {
    logToJitsu(
      this.analyticsService,
      'error',
      'yavin_terminal_payment_error',
      '[Yavin Terminal] Payment error',
      {
        orderId: order?.[OrderKeys.Id],
        orderNumber: order?.[OrderKeys.Number],
        errorMessage,
      },
    );

    errorMessage = 'Transaction annulée';
    this.loadKioskResetTimer = true;

    this.isLoading = false;
    this.errorMessage = errorMessage;
    this.statusMessage = 'ERREUR';
    this.isOnError = true;
  }

  private handlePaymentSuccess(
    order: DoodOrderModel,
    transactionResponse: YavinTransactionResponse,
  ): void {
    logToJitsu(
      this.analyticsService,
      'info',
      'yavin_terminal_payment_success',
      '[Yavin Terminal] Payment successful',
      {
        orderId: order[OrderKeys.Id],
        orderNumber: order[OrderKeys.Number],
        transactionId: transactionResponse.transactionId,
        amount: transactionResponse.amount,
      },
    );

    const clientCardTicketLines = transactionResponse.clientCardTicket.split('\n');

    this.yavinTerminalService
      .changeOrderStatusToPayment$(order.id, transactionResponse.transactionId ?? 'UNKNOWN')
      .pipe(
        tap(() => {
          this.isPaid = true;
          // Log status update success
          logToJitsu(
            this.analyticsService,
            'info',
            'yavin_terminal_status_update',
            '[Yavin Terminal] Status update to backend success',
            {
              orderId: order.id,
              paymentRef: transactionResponse.transactionId ?? 'UNKNOWN',
            },
          );
        }),
        tap(() => this.kioskService.printLines(clientCardTicketLines)),
        tap(order => this.handleChangeOrderStatusToPaymentSuccess(order)),
        catchError(err => {
          logToJitsu(
            this.analyticsService,
            'error',
            'yavin_terminal_status_update_error',
            '[Yavin Terminal] Failed to update order status',
            {
              orderId: order.id,
              paymentRef: transactionResponse.transactionId ?? 'UNKNOWN',
              error: err?.message,
            },
          );
          return throwError(() => err);
        }),
      )
      .subscribe();
  }

  private handleChangeOrderStatusToPaymentSuccess(order: DoodOrderModel): void {
    this.basketService.clearBasket();
    this.cartService.clearCart();
    this.terminalAbortService.unregister();
    this.router.navigate([
      this.routerHelper.translateRoute(`${Paths.Orders}/${order.id}/${Paths.Status}`),
    ]);
  }

  private handleOrderError(error: Partial<DoodApiError>): void {
    logToJitsu(
      this.analyticsService,
      'error',
      'yavin_terminal_order_error',
      '[Yavin Terminal] Order creation error',
      { errorMessage: error.detail ?? 'Une erreur est survenue.', detail: error.detail },
    );

    this.isLoading = false;
    this.errorMessage = error.detail ?? 'Une erreur est survenue.';
  }

  backToCart(): void {
    this.router.navigate([this.routerHelper.translateRoute(`${Paths.Cart}`)]);
  }
}
